import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import TransactionTable from "../components/TransactionTable";
import AddFinanceModal from "../components/AddFinanceModal";
import EditFinanceModal from "../components/EditFinanceModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { Search, Filter, Plus, Wallet } from "lucide-react";
import { accountService } from "../api/accountService";
import { useNotification } from "../contexts/NotificationContext";

export default function Finance() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const [isAdmin] = useState(() => {
    try {
      const data = JSON.parse(localStorage.getItem("user_data") || "{}");
      const roleStr = String(data?.role || data?.role_name || data?.role_id || "").toLowerCase();
      if (roleStr.includes("kasir") || roleStr === "cashier") {
        return false;
      }
      return (
        data?.is_admin === true ||
        data?.is_system_admin === true ||
        roleStr.includes("admin") ||
        roleStr.includes("owner")
      );
    } catch {
      return false;
    }
  });

  // Table State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [accounts, setAccounts] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const formatAccountType = (type) => {
    if (type === "PAYMENT_ACCOUNT") return "Kas/Rekening Pembayaran";
    if (type === "OPERATIONAL_ACCOUNT") return "Kas/Rekening Operasional";
    return type || "-";
  };

  const formatPaymentMethods = (account) => {
    if (account.name?.toLowerCase() === "laci kasir") return "Tunai";
    if (account.type === "OPERATIONAL_ACCOUNT") return "-";

    const raw = account.payment_methods || account.description || "";
    if (!raw) return "Transfer, QRIS, Campuran";

    const methods = raw.split(",").map((m) => m.trim().toUpperCase());
    const labels = [];
    if (methods.includes("TRANSFER")) labels.push("Transfer");
    if (methods.includes("QRIS")) labels.push("QRIS");
    if (methods.includes("CAMPURAN")) labels.push("Campuran");

    return labels.length > 0 ? labels.join(", ") : "Transfer, QRIS, Campuran";
  };

  // Fetch accounts from API
  useEffect(() => {
    console.log("🔄 Finance: Fetching accounts...", {
      currentPage,
      itemsPerPage,
      searchTerm,
      refreshTrigger,
    });
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await accountService.list({
          query: searchTerm,
          page: currentPage,
          limit: itemsPerPage,
        });

        const accountRows = response?.data || [];
        console.log(
          "✅ Finance: Accounts fetched successfully",
          accountRows.length,
        );

        // Map API response to table format
        const mappedAccounts = accountRows.map((account) => ({
          id: account.id,
          status: account.status === "ACTIVE" ? "Aktif" : "Non-Aktif",
          hasWarning: false,
          name: account.name,
          category: formatAccountType(account.type),
          type: account.type,
          payment_methods_label: formatPaymentMethods(account),
          payment_methods: account.description || account.payment_methods || "",
          balance: `Rp ${(account.total_balance || 0).toLocaleString("id-ID")}`,
          total_balance: account.total_balance,
          show_to_cashier: account.show_to_cashier,
          description: account.description,
          created_by: account.created_by,
          updated_by: account.updated_by,
          created_at: account.created_at,
          updated_at: account.updated_at,
        }));

        setAccounts(mappedAccounts);
        setTotalData(response.meta?.total || mappedAccounts.length);
      } catch (err) {
        console.error("Error fetching accounts:", err);
        setError(err.response?.data?.message || "Gagal memuat data akun");
        showError("Gagal memuat data akun");
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [currentPage, itemsPerPage, searchTerm, refreshTrigger]);

  // Pagination & Handlers
  const totalEntries = totalData;
  const maxPages = Math.ceil(totalEntries / itemsPerPage);
  const startEntry = totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);
  const paginatedAccounts = accounts;

  const handleAddNew = () => {
    setIsAddModalOpen(true);
  };

  const handleEdit = (id) => {
    const account = accounts.find((t) => t.id === id);
    setSelectedTransaction(account);
    setIsEditModalOpen(true);
  };

  const handleDelete = (id) => {
    const account = accounts.find((t) => t.id === id);
    setSelectedTransaction(account);
    setIsDeleteModalOpen(true);
  };

  const handleAddSubmit = async (data) => {
    try {
      console.log("➕ Creating account...");
      const payload = {
        name: data.name,
        status: data.status,
        type: data.type,
        payment_methods: data.payment_methods || "",
        show_to_cashier: data.show_to_cashier || false,
      };

      await accountService.create(payload);
      showSuccess("Akun berhasil ditambahkan");
      setIsAddModalOpen(false);
      console.log("✅ Account created, triggering refresh...");
      setRefreshTrigger((prev) => prev + 1); // Trigger refresh
    } catch (err) {
      console.error("Error creating account:", err);
      showError(err.response?.data?.message || "Gagal menambahkan akun");
    }
  };

  const handleEditSubmit = async (data) => {
    try {
      await accountService.update(data.id, {
        name: data.name,
        status: data.status,
        type: data.type,
        payment_methods: data.payment_methods || "",
        show_to_cashier: data.show_to_cashier || false,
      });
      showSuccess("Akun berhasil diperbarui");
      setIsEditModalOpen(false);
      setRefreshTrigger((prev) => prev + 1); // Trigger refresh
    } catch (err) {
      console.error("Error updating account:", err);
      showError(err.response?.data?.message || "Gagal memperbarui akun");
    }
  };

  const handleToggleCashier = async (tx) => {
    try {
      const newStatus = !tx.show_to_cashier;
      await accountService.update(tx.id, {
        name: tx.name,
        status: tx.status === "Aktif" ? "ACTIVE" : "INACTIVE",
        type: tx.type || "PAYMENT_ACCOUNT",
        description: tx.description || "",
        show_to_cashier: newStatus,
      });
      showSuccess(
        `Rekening "${tx.name}" ${
          newStatus ? "ditampilkan" : "disembunyikan"
        } di role Kasir`
      );
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Error updating cashier access:", err);
      showError(err.response?.data?.message || "Gagal mengubah status kasir");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await accountService.delete(selectedTransaction.id);
      showSuccess("Akun berhasil dihapus");
      setIsDeleteModalOpen(false);
      setRefreshTrigger((prev) => prev + 1); // Trigger refresh
    } catch (err) {
      console.error("Error deleting account:", err);
      showError(err.response?.data?.message || "Gagal menghapus akun");
    }
  };

  const handleInfo = (id) => {
    // Navigate to detail page
    navigate(`/keuangan/${id}`);
  };

  return (
    <div className="flex min-h-screen font-sans bg-slate-50">
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-all duration-300 pointer-events-none md:pointer-events-auto ${isSidebarCollapsed ? "w-16" : "w-48"}`}>
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
          activePage="keuangan"
        />
      </div>

      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
        />

        <main className="flex-1 p-6 overflow-x-hidden overflow-y-auto">
          {/* Top Card: Page Title & Actions */}
          <div className="p-5 mb-6 bg-white shadow-sm rounded-2xl ring-1 ring-slate-200/70">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              {/* Left: Page Title */}
              <div className="flex items-center w-full gap-4 md:w-auto">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-teal-50 shrink-0">
                  <Wallet className="w-6 h-6 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900">
                    Daftar Kas / Rekening
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Kelola transaksi keuangan toko Anda
                  </p>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex items-center gap-2.5 w-full md:w-auto">
                {/* Search dan Filter di-hide sesuai instruksi user */}
                {/*
                <div className="relative flex-1 md:w-64">
                  <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari data . . ."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full py-2 pr-4 text-sm transition-colors border rounded-lg pl-9 bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white"
                  />
                </div>

                <button className="p-2 transition-colors bg-white border rounded-lg border-slate-200 hover:bg-slate-50 hover:border-slate-300 text-slate-500 hover:text-slate-700">
                  <Filter className="w-4 h-4" />
                </button>
                */}

                {isAdmin && (
                  <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors bg-teal-600 rounded-lg shadow-sm hover:bg-teal-700 whitespace-nowrap shadow-teal-600/20">
                    <Plus className="w-4 h-4" />
                    <span>Tambah</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          <TransactionTable
            transactions={paginatedAccounts}
            searchTerm={searchTerm}
            sortBy={sortBy}
            sortOrder={sortOrder}
            loading={loading}
            error={error}
            totalEntries={totalEntries}
            currentPage={currentPage}
            maxPages={maxPages}
            startEntry={startEntry}
            endEntry={endEntry}
            itemsPerPage={itemsPerPage}
            onSearch={(val) => {
              setSearchTerm(val);
              setCurrentPage(1);
            }}
            onSort={(column) => {
              if (sortBy === column) {
                setSortOrder(sortOrder === "asc" ? "desc" : "asc");
              } else {
                setSortBy(column);
                setSortOrder("asc");
              }
            }}
            onEdit={isAdmin ? handleEdit : undefined}
            onDelete={isAdmin ? handleDelete : undefined}
            onInfo={handleInfo}
            onToggleCashier={isAdmin ? handleToggleCashier : undefined}
            showCashierColumn={isAdmin}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newPerPage) => {
              setItemsPerPage(newPerPage);
              setCurrentPage(1);
            }}
          />
        </main>
      </div>

      <AddFinanceModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddSubmit}
      />

      <EditFinanceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        data={selectedTransaction}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        data={selectedTransaction}
      />
    </div>
  );
}
